import { useEffect, useState, useRef } from "react";
import { useSettings } from "@/context/SettingsContext";
import { Plus, Edit2, Trash2, Mail, Phone, Shield, Eye, Receipt as ReceiptIcon, Printer, FileSpreadsheet, FileDown, Search, Filter } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import ReceiptPrint from "@/components/ReceiptPrint";
import { printReceipt, downloadReceiptPDF, shareReceiptPDF, numberToWords } from "@/utils/printUtils";
import { exportMembersToExcel, exportMembersListToPDF } from "@/utils/exportUtils";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api from "@/utils/api";

const Members = () => {
  const navigate = useNavigate();
  const { formatCurrency, formatDate, settings } = useSettings();
  const receiptRef = useRef();
  const [members, setMembers] = useState([]);
  const [filteredMembers, setFilteredMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [printReceiptMember, setPrintReceiptMember] = useState(null);
  const [showPrintDialog, setShowPrintDialog] = useState(false);
  const [admissionFeeReceiptExists, setAdmissionFeeReceiptExists] = useState(false);
  
  // Search and Filter states
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [admissionFilter, setAdmissionFilter] = useState("all");

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    emergency_contact: "",
    password: "",
    gender: "",
    date_of_birth: "",
    height: "",
    weight: "",
    admission_fee_paid: false,
    admission_fee_amount: "",
    admission_fee_date: new Date().toISOString().split('T')[0],
  });

  useEffect(() => {
    fetchMembers();
  }, []);

  useEffect(() => {
    filterMembers();
  }, [members, searchQuery, statusFilter, admissionFilter]);

  const filterMembers = () => {
    let result = [...members];

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(member => 
        member.name.toLowerCase().includes(query) ||
        member.email.toLowerCase().includes(query) ||
        member.phone.includes(query)
      );
    }

    // Status filter
    if (statusFilter !== "all") {
      result = result.filter(member => member.status === statusFilter);
    }

    // Admission Fee filter
    if (admissionFilter !== "all") {
      if (admissionFilter === "paid") {
        result = result.filter(member => member.admission_fee_paid);
      } else {
        result = result.filter(member => !member.admission_fee_paid);
      }
    }

    setFilteredMembers(result);
  };

  const fetchMembers = async () => {
    try {
      const response = await api.get('/members');
      setMembers(response.data);
    } catch (error) {
      toast.error("Failed to load members");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingMember) {
        const prevPaid = !!editingMember.admission_fee_paid;
        const nowPaid = !!formData.admission_fee_paid;
        const amountVal = parseFloat(formData.admission_fee_amount || 0);
        const payload = { ...formData };
        if (!payload.password) {
          delete payload.password;
        }
        const updateRes = await api.patch(`/members/${editingMember.id}`, payload);
        toast.success("Member updated successfully");

        if (!prevPaid && nowPaid && amountVal > 0 && !admissionFeeReceiptExists) {
          try {
            await api.post('/receipts', {
              receipt_type: 'admission_fee',
              member_id: editingMember.id,
              amount: amountVal,
              payment_method: 'cash',
              description: 'Admission Fee',
              receipt_date: formData.admission_fee_date
            });
            toast.success("Admission fee receipt recorded in ledger");
          } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to record admission fee receipt");
          }
        } else if (!prevPaid && nowPaid && admissionFeeReceiptExists) {
          toast.info("Admission fee receipt already exists for this member");
        }
      } else {
        const amountVal = parseFloat(formData.admission_fee_amount || 0);
        const createRes = await api.post('/members', formData);
        toast.success("Member added successfully");

        if (formData.admission_fee_paid && amountVal > 0) {
          try {
            const receiptsRes = await api.get(`/members/${createRes.data.id}/receipts`);
            const exists = Array.isArray(receiptsRes.data) && receiptsRes.data.some(r => r.receipt_type === 'admission_fee');
            if (!exists) {
              await api.post('/receipts', {
                receipt_type: 'admission_fee',
                member_id: createRes.data.id,
                amount: amountVal,
                payment_method: 'cash',
                description: 'Admission Fee',
                receipt_date: formData.admission_fee_date
              });
              toast.success("Admission fee receipt recorded in ledger");
            } else {
              toast.info("Admission fee receipt already exists for this member");
            }
          } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to record admission fee receipt");
          }
        }
      }
      fetchMembers();
      setIsDialogOpen(false);
      resetForm();
    } catch (error) {
      toast.error("Failed to save member");
    }
  };

  const handleEdit = async (member) => {
    setEditingMember(member);
    setFormData({
      name: member.name,
      email: member.email,
      phone: member.phone,
      emergency_contact: member.emergency_contact,
      password: "",
      gender: member.gender || "",
      date_of_birth: member.date_of_birth ? member.date_of_birth.split('T')[0] : "",
      height: member.height || "",
      weight: member.weight || "",
      admission_fee_paid: member.admission_fee_paid || false,
      admission_fee_amount: member.admission_fee_amount || "",
      admission_fee_date: new Date().toISOString().split('T')[0],
    });
    try {
      const receiptsRes = await api.get(`/members/${member.id}/receipts`);
      const receipt = Array.isArray(receiptsRes.data) && receiptsRes.data.find(r => r.receipt_type === 'admission_fee');
      setAdmissionFeeReceiptExists(!!receipt);
      if (receipt && receipt.receipt_date) {
        setFormData(prev => ({
          ...prev,
          admission_fee_date: receipt.receipt_date.split('T')[0]
        }));
      }
    } catch (e) {
      setAdmissionFeeReceiptExists(false);
    }
    setIsDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this member?")) {
      try {
        await api.delete(`/members/${id}`);
        toast.success("Member deleted successfully");
        fetchMembers();
      } catch (error) {
        toast.error("Failed to delete member");
      }
    }
  };

  const resetForm = () => {
    setFormData({
      name: "",
      email: "",
      phone: "",
      emergency_contact: "",
      password: "",
      gender: "",
      date_of_birth: "",
      height: "",
      weight: "",
      admission_fee_paid: false,
      admission_fee_amount: "",
      admission_fee_date: new Date().toISOString().split('T')[0],
    });
    setEditingMember(null);
    setAdmissionFeeReceiptExists(false);
  };

  const handlePrintAdmissionReceipt = (member) => {
    if (!member.admission_fee_paid) {
      toast.error("Admission fee not paid yet");
      return;
    }
    
    const user = JSON.parse(localStorage.getItem('user'));
    const company = user?.company;
    
    const receiptData = {
      receipt_number: `ADM-${member.id.slice(0, 8).toUpperCase()}`,
      receipt_date: member.admission_fee_payment_date || member.join_date,
      receipt_type: "admission_fee",
      amount: member.admission_fee_amount,
      payment_method: "Cash",
      description: "Admission Fee",
      member_name: member.name,
      member_email: member.email,
      member_phone: member.phone,
      company_name: company?.name,
      company_address: company?.address,
      company_phone: company?.phone,
      company_email: company?.email,
      company_logo_url: company?.logo_url,
      amount_in_words: numberToWords(member.admission_fee_amount)
    };
    
    setPrintReceiptMember(receiptData);
    setShowPrintDialog(true);
  };

  const handlePrint = (format) => {
    if (receiptRef.current) {
      printReceipt(receiptRef.current, format);
    }
  };

  const handleDownloadPDF = async (format) => {
    if (receiptRef.current && printReceiptMember) {
      const success = await downloadReceiptPDF(
        receiptRef.current,
        `admission-receipt-${printReceiptMember.receipt_number}.pdf`,
        format
      );
      if (success) {
        toast.success("Receipt downloaded successfully");
      }
    }
  };

  const handleSharePDF = async (format) => {
    if (receiptRef.current && printReceiptMember) {
      const success = await shareReceiptPDF(
        receiptRef.current,
        `admission-receipt-${printReceiptMember.receipt_number}.pdf`,
        format
      );
      if (success) {
        toast.success("Receipt shared successfully");
      }
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-lime-400"></div>
      </div>
    );
  }

  return (
    <div data-testid="members-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4 mb-6">
        <div>
          <h1 className="text-4xl font-black tracking-tight mb-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
            Members
          </h1>
          <p className="text-zinc-500">Manage members</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            onClick={() => exportMembersToExcel(members, formatCurrency, formatDate) && toast.success("Exported to Excel")}
            variant="outline"
            className="gap-2"
          >
            <FileSpreadsheet size={16} />
            Export Excel
          </Button>
          <Button
            onClick={async () => {
              const tableEl = document.querySelector('.members-table-desktop');
              if (tableEl) {
                const success = await exportMembersListToPDF(tableEl, 'Members List');
                if (success) toast.success("Exported to PDF");
              }
            }}
            variant="outline"
            className="gap-2"
          >
            <FileDown size={16} />
            Export PDF
          </Button>
          <Dialog open={isDialogOpen} onOpenChange={(open) => {
            setIsDialogOpen(open);
            if (!open) resetForm();
          }}>
            <DialogTrigger asChild>
              <Button 
                data-testid="add-member-btn"
                className="bg-lime-400 text-zinc-900 hover:bg-lime-500 font-bold shadow-sm transition-all active:scale-95"
              >
                <Plus size={20} className="mr-2" />
                Add Member
              </Button>
            </DialogTrigger>
          <DialogContent aria-describedby="member-dialog-description" className="max-h-[90vh] overflow-y-auto max-w-2xl">
            <DialogHeader>
              <DialogTitle>{editingMember ? "Edit Member" : "Add New Member"}</DialogTitle>
            </DialogHeader>
            <p id="member-dialog-description" className="sr-only">
              {editingMember ? "Edit member information" : "Add a new member to the gym"}
            </p>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="name">Name *</Label>
                  <Input
                    id="name"
                    data-testid="member-name-input"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="email">Email *</Label>
                  <Input
                    id="email"
                    type="email"
                    data-testid="member-email-input"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="phone">Phone *</Label>
                  <Input
                    id="phone"
                    data-testid="member-phone-input"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="emergency_contact">Emergency Contact *</Label>
                  <Input
                    id="emergency_contact"
                    data-testid="member-emergency-input"
                    value={formData.emergency_contact}
                    onChange={(e) => setFormData({ ...formData, emergency_contact: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="password">Password {editingMember ? '' : '*'} </Label>
                  <Input
                    id="password"
                    type="password"
                    placeholder={editingMember ? "Leave blank to keep current password" : "Member login password"}
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    required={!editingMember}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="gender">Gender</Label>
                  <select
                    id="gender"
                    value={formData.gender}
                    onChange={(e) => setFormData({ ...formData, gender: e.target.value })}
                    className="w-full h-11 px-3 bg-zinc-50 border border-zinc-200 rounded-md focus:ring-2 focus:ring-lime-400/50 focus:border-lime-400 transition-all"
                  >
                    <option value="">Select Gender</option>
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <div>
                  <Label htmlFor="date_of_birth">Date of Birth</Label>
                  <input
                    type="date"
                    id="date_of_birth"
                    value={formData.date_of_birth}
                    onChange={(e) => setFormData({ ...formData, date_of_birth: e.target.value })}
                    className="w-full h-11 px-3 bg-zinc-50 border border-zinc-200 rounded-md focus:ring-2 focus:ring-lime-400/50 focus:border-lime-400 transition-all"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="height">Height (cm)</Label>
                  <Input
                    id="height"
                    type="number"
                    step="0.1"
                    placeholder="e.g., 175"
                    value={formData.height}
                    onChange={(e) => setFormData({ ...formData, height: e.target.value })}
                  />
                </div>
                <div>
                  <Label htmlFor="weight">Weight (kg)</Label>
                  <Input
                    id="weight"
                    type="number"
                    step="0.1"
                    placeholder="e.g., 70"
                    value={formData.weight}
                    onChange={(e) => setFormData({ ...formData, weight: e.target.value })}
                  />
                </div>
              </div>

              <div className="border-t border-zinc-200 pt-4">
                <h3 className="text-sm font-bold text-zinc-900 mb-3">Admission Fee</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="admission_fee_amount">Admission Fee Amount ($)</Label>
                    <Input
                      id="admission_fee_amount"
                      type="number"
                      step="0.01"
                      placeholder="e.g., 100"
                      value={formData.admission_fee_amount}
                      onChange={(e) => setFormData({ ...formData, admission_fee_amount: e.target.value })}
                    />
                  </div>
                  <div className="flex flex-col gap-2">
                    <div className="flex items-end h-11">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.admission_fee_paid}
                          onChange={(e) => setFormData({ ...formData, admission_fee_paid: e.target.checked })}
                          disabled={admissionFeeReceiptExists}
                          className="w-4 h-4 text-lime-600 bg-zinc-50 border-zinc-300 rounded focus:ring-lime-500"
                        />
                        <span className="text-sm font-medium text-zinc-700">Admission Fee Paid</span>
                      </label>
                    </div>
                    {formData.admission_fee_paid && (
                      <div>
                        <Label htmlFor="admission_fee_date">Payment Date</Label>
                        <input
                          type="date"
                          id="admission_fee_date"
                          value={formData.admission_fee_date}
                          onChange={(e) => setFormData({ ...formData, admission_fee_date: e.target.value })}
                          className="w-full h-9 px-3 bg-zinc-50 border border-zinc-200 rounded-md focus:ring-2 focus:ring-lime-400/50 focus:border-lime-400 transition-all text-sm"
                          disabled={admissionFeeReceiptExists}
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <Button 
                type="submit" 
                data-testid="member-submit-btn"
                className="w-full bg-lime-400 text-zinc-900 hover:bg-lime-500 font-bold"
              >
                {editingMember ? "Update" : "Add"} Member
              </Button>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="flex flex-col md:flex-row gap-4 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" size={18} />
          <Input 
            placeholder="Search by name, email or phone..." 
            className="pl-10"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <div className="flex gap-4">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="h-10 px-3 bg-white border border-zinc-200 rounded-md text-sm focus:ring-2 focus:ring-lime-400/50 focus:border-lime-400 outline-none min-w-[150px]"
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
          <select
            value={admissionFilter}
            onChange={(e) => setAdmissionFilter(e.target.value)}
            className="h-10 px-3 bg-white border border-zinc-200 rounded-md text-sm focus:ring-2 focus:ring-lime-400/50 focus:border-lime-400 outline-none min-w-[150px]"
          >
            <option value="all">All Admissions</option>
            <option value="paid">Fee Paid</option>
            <option value="unpaid">Fee Unpaid</option>
          </select>
        </div>
      </div>

      {/* Members Table - Desktop */}
      <div className="hidden md:block bg-white border border-zinc-200 rounded-lg overflow-x-auto shadow-sm members-table-desktop">
        <table className="w-full whitespace-nowrap">
          <thead className="bg-zinc-50 text-zinc-500 font-medium text-xs uppercase tracking-wider">
            <tr>
              <th className="px-6 py-4 text-left">S.No</th>
              <th className="px-6 py-4 text-left">Name</th>
              <th className="px-6 py-4 text-left">Contact</th>
              <th className="px-6 py-4 text-left hidden lg:table-cell">Details</th>
              <th className="px-6 py-4 text-left">Join Date</th>
              <th className="px-6 py-4 text-left">Admission Fee</th>
              <th className="px-6 py-4 text-left">Status</th>
              <th className="px-6 py-4 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredMembers.length === 0 ? (
              <tr>
                <td colSpan="8" className="px-6 py-12 text-center text-zinc-500">
                  {members.length === 0 
                    ? "No members found. Add your first member to get started." 
                    : "No members found matching your filters."}
                </td>
              </tr>
            ) : (
              filteredMembers.map((member, index) => (
                <tr
                  key={member.id}
                  data-testid={`member-row-${member.id}`}
                  onClick={() => navigate(`/members/${member.id}`)}
                  className="hover:bg-zinc-50 transition-colors border-b border-zinc-100 last:border-0 cursor-pointer"
                >
                  <td className="px-6 py-4 font-medium text-zinc-500">
                    {index + 1}
                  </td>
                  <td className="px-6 py-4">
                    <div className="font-medium text-zinc-900">{member.name}</div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex flex-col gap-1 text-sm">
                      <div className="flex items-center gap-2 text-zinc-600">
                        <Mail size={14} />
                        {member.email}
                      </div>
                      <div className="flex items-center gap-2 text-zinc-600">
                        <Phone size={14} />
                        {member.phone}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 hidden lg:table-cell">
                    <div className="flex flex-col gap-1 text-xs text-zinc-600">
                      <div className="flex items-center gap-2">
                        <Shield size={12} />
                        <span>Emergency: {member.emergency_contact}</span>
                      </div>
                      {member.gender && (
                        <div className="capitalize">{member.gender}</div>
                      )}
                      {(member.height || member.weight) && (
                        <div>
                          {member.height && `${member.height}cm`}
                          {member.height && member.weight && ' • '}
                          {member.weight && `${member.weight}kg`}
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-zinc-600">
                    {formatDate(member.join_date)}
                  </td>
                  <td className="px-6 py-4">
                    {member.admission_fee_amount ? (
                      <div className={`inline-flex items-center px-2.5 py-1 rounded text-xs font-medium ${
                        member.admission_fee_paid
                          ? 'bg-green-100 text-green-700'
                          : 'bg-orange-100 text-orange-700'
                      }`}>
                        {formatCurrency(member.admission_fee_amount)}
                        {member.admission_fee_paid ? ' ✓' : ' Due'}
                      </div>
                    ) : (
                      <span className="text-xs text-zinc-400">-</span>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      member.status === 'active' 
                        ? 'bg-lime-100 text-lime-800' 
                        : 'bg-zinc-100 text-zinc-800'
                    }`}>
                      {member.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-right">
                    <div className="flex justify-end gap-2">
                      <button
                        data-testid={`edit-member-${member.id}`}
                        onClick={(e) => { e.stopPropagation(); handleEdit(member); }}
                        className="p-2 hover:bg-zinc-100 rounded-md transition-colors"
                      >
                        <Edit2 size={16} className="text-zinc-600" />
                      </button>
                      <button
                        data-testid={`delete-member-${member.id}`}
                        onClick={(e) => { e.stopPropagation(); handleDelete(member.id); }}
                        className="p-2 hover:bg-red-50 rounded-md transition-colors"
                      >
                        <Trash2 size={16} className="text-red-600" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Members Cards - Mobile */}
      <div className="md:hidden space-y-4">
        {filteredMembers.length === 0 ? (
          <div className="bg-white border border-zinc-200 rounded-lg p-8 text-center text-zinc-500">
            {members.length === 0 
              ? "No members found. Add your first member to get started." 
              : "No members found matching your filters."}
          </div>
        ) : (
          filteredMembers.map((member) => (
            <div
              key={member.id}
              data-testid={`member-card-${member.id}`}
              onClick={() => navigate(`/members/${member.id}`)}
              className="bg-white border border-zinc-200 rounded-lg p-4 shadow-sm cursor-pointer"
            >
              {/* Header */}
              <div className="flex justify-between items-start mb-3">
                <div className="flex-1">
                  <h3 className="font-bold text-zinc-900 text-lg">{member.name}</h3>
                  <div className="flex items-center gap-2 text-xs text-zinc-500 mt-1">
                    <Mail size={12} />
                    {member.email}
                  </div>
                  <div className="flex items-center gap-2 text-xs text-zinc-500 mt-1">
                    <Phone size={12} />
                    {member.phone}
                  </div>
                </div>
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                  member.status === 'active' 
                    ? 'bg-lime-100 text-lime-800' 
                    : 'bg-zinc-100 text-zinc-800'
                }`}>
                  {member.status}
                </span>
              </div>

              {/* Details */}
              <div className="space-y-2 mb-3 pb-3 border-b border-zinc-100">
                <div className="flex justify-between text-sm">
                  <span className="text-zinc-500">Join Date:</span>
                  <span className="text-zinc-900 font-medium">{formatDate(member.join_date)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-zinc-500">Admission Fee:</span>
                  {member.admission_fee_amount ? (
                    <div className={`inline-flex items-center px-2.5 py-1 rounded text-xs font-medium ${
                      member.admission_fee_paid
                        ? 'bg-green-100 text-green-700'
                        : 'bg-orange-100 text-orange-700'
                    }`}>
                      {formatCurrency(member.admission_fee_amount)}
                      {member.admission_fee_paid ? ' ✓' : ' Due'}
                    </div>
                  ) : (
                    <span className="text-xs text-zinc-400">-</span>
                  )}
                </div>
                {member.emergency_contact && (
                  <div className="flex justify-between text-sm">
                    <span className="text-zinc-500">Emergency:</span>
                    <span className="text-zinc-900 text-xs">{member.emergency_contact}</span>
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={(e) => { e.stopPropagation(); handleEdit(member); }}
                  className="flex-1"
                >
                  <Edit2 size={14} className="mr-1" />
                  Edit
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={(e) => { e.stopPropagation(); handleDelete(member.id); }}
                  className="px-3 text-red-600 hover:text-red-700"
                >
                  <Trash2 size={14} />
                </Button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Print Dialog */}
      {showPrintDialog && printReceiptMember && (
        <Dialog open={showPrintDialog} onOpenChange={setShowPrintDialog}>
          <DialogContent aria-describedby="admission-print-dialog">
            <DialogHeader>
              <DialogTitle>Print Admission Fee Receipt</DialogTitle>
            </DialogHeader>
            <p id="admission-print-dialog" className="text-sm text-zinc-600 mb-4">
              {printReceiptMember.member_name} - {formatCurrency(printReceiptMember.amount)}
            </p>
            
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <Button onClick={() => handlePrint('a4')} className="bg-blue-500 hover:bg-blue-600">
                  <Printer size={16} className="mr-2" />
                  Print A4
                </Button>
                <Button onClick={() => handlePrint('a5')} className="bg-blue-500 hover:bg-blue-600">
                  <Printer size={16} className="mr-2" />
                  Print A5
                </Button>
              </div>
              <Button onClick={() => handlePrint('thermal')} className="w-full bg-purple-500 hover:bg-purple-600">
                <Printer size={16} className="mr-2" />
                Print Thermal
              </Button>
              <div className="border-t pt-3">
                <div className="grid grid-cols-2 gap-3">
                  <Button onClick={() => handleDownloadPDF('a4')} variant="outline">
                    Download PDF
                  </Button>
                  <Button onClick={() => handleSharePDF('a4')} variant="outline">
                    Share PDF
                  </Button>
                </div>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Hidden Receipt */}
      {printReceiptMember && (
        <ReceiptPrint ref={receiptRef} receipt={printReceiptMember} receiptType="admission" />
      )}
    </div>
  );
};

export default Members;
